import React, { Fragment } from 'react';
import _ from 'lodash';
import {
    Card,
    CardBody,
    CardHeader,
    Collapse,
    FormGroup,
    Input,
    Label
} from 'reactstrap';
import qs from 'qs';
import { withRouter } from 'react-router-dom';

import { urlify } from '../../utils';
import CollapseButton from '../dumb/collapseButton';
import baseFilter from './baseFilter';


class TissueFilter extends baseFilter {

    constructor(props) {
        super(props);

        this.toggleCheckbox = this.toggleCheckbox.bind(this);
        this.toggleCollapse = this.toggleCollapse.bind(this);
        this.processResponse = this.processResponse.bind(this);
        this.updateAPIURL = this.updateAPIURL.bind(this);

        const current_qs = qs.parse(
            window.location.search,
            { ignoreQueryPrefix: true }
        );

        this.state = {
            isOpen: false,

            /*
             * Tissue options are discovered dynamically from the
             * aggregate response.
             */
            filters: TissueFilter.getFiltersFromQS(
                current_qs,
                {}
            ),

            activeCount: 0
        };

        /*
         * Ask the API for counts grouped by tissue name.
         */
        this.aggregates = {
            'sample.tissue.name': 'count'
        };

        // Guards against re-fetching more than once when the URL
        // already has a tissue filter on first mount (see
        // processResponse for why this is needed).
        this.hasSyncedInitialSelection = false;
    }


    /*
     * Read active tissue filters from the URL and apply them
     * to the currently known filters.
     */
    static getFiltersFromQS(qsObj, oldfilters) {

        const qsTissueFilter =
            ('tissue' in qsObj)
                ? _.split(qsObj.tissue, '_')
                : [];

        return _.reduce(
            oldfilters,
            (res, filterProps, tissue) => {

                res[tissue] = {
                    ...filterProps,

                    active:
                        _.indexOf(
                            qsTissueFilter,
                            urlify(tissue)
                        ) >= 0
                };

                return res;
            },
            {}
        );
    }


    static getDerivedStateFromProps(newprops, oldstate) {

        const current_qs = qs.parse(
            newprops.location.search,
            { ignoreQueryPrefix: true }
        );

        const newfilters = TissueFilter.getFiltersFromQS(
            current_qs,
            oldstate.filters
        );

        return {
            filters: newfilters,
            activeCount: _.filter(
                newfilters,
                'active'
            ).length
        };
    }


    /*
     * Add the selected tissues to the API request.
     */
    updateAPIURL(current_api_qs, newlistprops) {

        const new_qs = qs.parse(
            newlistprops.location.search,
            { ignoreQueryPrefix: true }
        );

        const qsTissueFilter = TissueFilter.getFiltersFromQS(
            new_qs,
            this.state.filters
        );

        /*
         * IMPORTANT:
         *
         * filter_name contains the actual tissue name expected
         * by the API, while the URL contains the urlified value.
         */
        const apiTissueFilters = _.map(
            _.filter(
                _.toPairs(qsTissueFilter),
                ([tissue, filterProps]) => {
                    return filterProps.active;
                }
            ),
            ([tissue, filterProps]) => {
                return filterProps.filter_name || tissue;
            }
        );

        const filter_qs = {
            name: 'sample',
            op: 'has',
            val: {
                name: 'tissue',
                op: 'has',
                val: {
                    name: 'name',
                    op: 'in',
                    val: apiTissueFilters
                }
            }
        };

        if (apiTissueFilters.length > 0) {

            current_api_qs.filter = [
                ...(current_api_qs.filter || []),
                filter_qs
            ];
        }

        /*
         * Always request the tissue aggregate so that the checkbox
         * counts can be populated.
         */
        return _.merge(
            current_api_qs,
            {
                agg: this.aggregates
            }
        );
    }


    /*
     * Process the aggregate response.
     *
     * Example expected aggregate:
     *
     * sample.tissue.name:
     * {
     *     count: {
     *         Breast: 100,
     *         Lung: 80,
     *         Colon: 50
     *     }
     * }
     */
    processResponse(response) {

        const resp_agg = this.get_agg_values(response);
        const agg_field = _.keys(this.aggregates)[0];

        if (!resp_agg) {
            return;
        }

        const counts = _.get(
            resp_agg,
            [
                agg_field,
                this.aggregates[agg_field]
            ],
            {}
        );

        /*
         * On a fresh mount where the URL already has an active
         * tissue filter (e.g. arriving from a direct link), the
         * very first request goes out before this.state.filters
         * has ever been populated -- so updateAPIURL has no
         * filter_name to send and that first request is
         * unfiltered. This aggregate response is what makes the
         * translation from urlified value -> real tissue name
         * possible for the first time.
         */
        const wasFirstPopulation = _.isEmpty(this.state.filters);

        const newfilters = _.reduce(
            counts,
            (res, count, tissue) => {

                /*
                 * Ignore empty aggregate buckets.
                 */
                if (
                    tissue === undefined ||
                    tissue === null ||
                    tissue === ''
                ) {
                    return res;
                }

                res[tissue] = {
                    /*
                     * Preserve active state if this tissue already
                     * existed in state.
                     */
                    ...(this.state.filters[tissue] || {
                        active: false
                    }),

                    /*
                     * The actual value sent to the API.
                     */
                    filter_name: tissue,

                    /*
                     * Make sure the displayed number is numeric.
                     */
                    count: Number(count) || 0
                };

                return res;

            },
            {}
        );

        /*
         * Re-apply the URL state after rebuilding the filters.
         * This prevents the aggregate response from accidentally
         * clearing checked boxes.
         */
        const current_qs = qs.parse(
            window.location.search,
            { ignoreQueryPrefix: true }
        );

        const finalFilters = TissueFilter.getFiltersFromQS(
            current_qs,
            newfilters
        );

        /*
         * If an active tissue in the URL can now be resolved
         * against a real tissue name for the first time, the
         * first request above went out without it applied --
         * fire exactly one follow-up request now that it's
         * resolvable.
         */
        const activeTissues =
            ('tissue' in current_qs)
                ? _.split(current_qs.tissue, '_')
                : [];

        const hasNewlyResolvableFilter = activeTissues.some(
            activeValue => _.some(
                _.keys(counts),
                tissue => urlify(tissue) === activeValue
            )
        );

        const shouldSyncInitialSelection =
            wasFirstPopulation &&
            hasNewlyResolvableFilter &&
            !this.hasSyncedInitialSelection;

        if (shouldSyncInitialSelection) {
            this.hasSyncedInitialSelection = true;
        }

        this.setState({
            filters: finalFilters,
            activeCount: _.filter(
                finalFilters,
                'active'
            ).length
        }, () => {
            if (shouldSyncInitialSelection) {
                this.props.requestAggregation();
            }
        });
    }


    /*
     * Checkbox handling.
     */
    toggleCheckbox(event) {

        const tissue = event.target.name;

        /*
         * URL representation.
         */
        const urlTissue = urlify(tissue);

        const current_qs = qs.parse(
            window.location.search,
            { ignoreQueryPrefix: true }
        );

        let tissueFilter =
            ('tissue' in current_qs)
                ? _.split(current_qs.tissue, '_')
                : [];

        if (event.target.checked) {

            /*
             * Don't add duplicates.
             */
            if (!tissueFilter.includes(urlTissue)) {
                tissueFilter.push(urlTissue);
            }

        } else {

            tissueFilter = tissueFilter.filter(
                (item) => urlify(item) !== urlTissue
            );
        }

        current_qs.tissue =
            tissueFilter.length > 0
                ? _.join(
                    tissueFilter.sort(),
                    '_'
                )
                : undefined;

        /*
         * Reset pagination when changing filters.
         */
        current_qs.page = undefined;

        this.props.history.push(
            window.location.pathname +
            qs.stringify(
                current_qs,
                { addQueryPrefix: true }
            )
        );
    }


    toggleCollapse() {

        this.setState({
            isOpen: !this.state.isOpen
        });
    }


    renderTissueCheckboxes() {

        if (_.isUndefined(this.state.filters)) {
            return null;
        }

        let active = [];
        let inactive = [];

        _.map(
            _.toPairs(this.state.filters),
            ([key, filterProps]) => {

                const count =
                    Number(filterProps.count) || 0;

                const cb = (
                    <FormGroup
                        check
                        key={key}
                    >

                        <Input
                            type="checkbox"
                            name={key}
                            onChange={this.toggleCheckbox}
                            checked={!!filterProps.active}
                        />

                        {' '}

                        <Label
                            className="ml-4 mt-1"
                            check
                        >
                            {key}

                            <span className="secondary">
                                {' '}({count})
                            </span>
                        </Label>

                    </FormGroup>
                );

                if (filterProps.active) {
                    active.push(cb);
                } else {
                    inactive.push(cb);
                }
            }
        );


        /*
         * Keep active filters at the top.
         *
         * Then show enough inactive filters to make five
         * visible before the rest.
         */
        if (
            active.length < 5 &&
            active.length + inactive.length > 0
        ) {
            active = active.concat(
                inactive.splice(
                    0,
                    5 - active.length
                )
            );
        }


        return (
            <Fragment>
                {active}
                {inactive}
            </Fragment>
        );
    }


    componentDidMount() {

        this.props.registerResponse(
            this.processResponse
        );

        this.props.registerRequest(
            this.updateAPIURL
        );
    }


    componentWillUnmount() {

        this.props.unregisterResponse(
            this.processResponse
        );

        this.props.unregisterRequest(
            this.updateAPIURL
        );
    }


    render() {

        return (
            <Card className="mb-2 z0 hover">

                <CardHeader
                    className="justify-content-between d-flex"
                    onClick={this.toggleCollapse}
                >

                    <div className="d-inline w-50">
                        Tissues
                    </div>

                    <div className="d-inline-block">

                        {this.state.activeCount > 0 && (
                            <strong>
                                {this.state.activeCount + ' active'}
                            </strong>
                        )}

                    </div>

                    <CollapseButton
                        isOpen={this.state.isOpen}
                        onClick={this.toggleCollapse}
                    />

                </CardHeader>

                <Collapse
                    isOpen={this.state.isOpen}
                >

                    <CardBody>
                        {this.renderTissueCheckboxes()}
                    </CardBody>

                </Collapse>

            </Card>
        );
    }
}


export default withRouter(TissueFilter);